import { useCallback, useContext, useEffect } from 'react'

import { MEDIA_QUERIES } from 'design'

import { useCallManager } from '../../../contexts/call_manager_context'
import usePreviousProps from '../../../hooks/previous_props_hook'
import { useMediaQuery } from '../../../hooks/use_media_query'
import { AppLocationContext } from '../../../pages/admin/app_location_provider'

// nav to "video view" on the first video/screen-share enable
// nav to "chat view" on the last video/screen-share disable
export const useAutoNavCallViews = (isVideoActive: boolean): (() => void) => {
  const {
    activeCall: { navToVideoView, navToChatView, callRecord },
  } = useCallManager()

  const prevIsVideoActive = usePreviousProps(isVideoActive)

  const { appLocation } = useContext(AppLocationContext)
  const desktop = useMediaQuery(MEDIA_QUERIES.md)

  const autoNav = useCallback(() => {
    if (isVideoActive) {
      navToVideoView && navToVideoView()
    } else {
      if (!desktop) {
        return
      }

      navToChatView && navToChatView()
    }
  }, [desktop, isVideoActive, navToChatView, navToVideoView])

  useEffect(() => {
    if (appLocation.topicId !== callRecord?.topic_id) {
      return
    }

    // Automatically switching views on smallest breakpoints creates a jarring UX, so if we're on these small breakpoints,
    // avoid automatic switching - this will resolve issues around camera switching
    const shouldNavToChatView =
      callRecord && prevIsVideoActive && !isVideoActive && desktop
    if (shouldNavToChatView) {
      navToChatView && navToChatView()
    }

    // If we're on a small breakpoint, we should still switch to video view if possible, because it isn't
    // easy to tell if video has started when you're on chat view
    const shouldNavToVideoView = !prevIsVideoActive && isVideoActive
    if (shouldNavToVideoView) {
      navToVideoView && navToVideoView()
    }
  }, [
    appLocation.topicId,
    callRecord,
    desktop,
    isVideoActive,
    navToChatView,
    navToVideoView,
    prevIsVideoActive,
  ])

  return autoNav
}
